import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { 
  X, Bell, AlertTriangle, MessageSquare, 
  ChevronLeft, ChevronRight, Clock, Volume2, VolumeX
} from "lucide-react";
import { useBrowserNotifications } from "../hooks/useBrowserNotifications";

/**
 * NotificationPopup - Viser in-app notifikationer på kortet og i UI
 * Bruges til at vise vigtige beskeder til frivillige
 * Nu med browser push notifikationer når logget ind
 */
export default function NotificationPopup({ eventId, user, onClose, position = "map" }) {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());
  const previousNotificationCountRef = useRef(0);
  
  const token = localStorage.getItem("token");
  const isLoggedIn = !!token && !!user;
  
  // Browser notifikationer hook
  const {
    permission,
    isSupported,
    soundEnabled,
    setSoundEnabled,
    requestPermission,
    showNotification
  } = useBrowserNotifications(eventId, token, isLoggedIn);

  const loadNotifications = useCallback(async () => {
    if (!eventId || !token) return;
    
    try {
      const res = await axios.get(`${API}/events/${eventId}/notifications/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newNotifications = res.data;
      
      // Check for nye notifikationer og vis browser notification
      if (isLoggedIn && permission === 'granted' && newNotifications.length > 0) {
        const previousIds = new Set(notifications.map(n => n.id));
        const brandNewNotifications = newNotifications.filter(n => !previousIds.has(n.id));
        
        // Vis browser notifikation for hver ny besked
        brandNewNotifications.forEach(notif => {
          showNotification(
            notif.subject || 'Ny besked',
            notif.content?.substring(0, 100) || '',
            notif.type || 'normal'
          );
        });
      }
      
      setNotifications(newNotifications);
    } catch (error) {
      console.error("Fejl ved hentning af notifikationer:", error);
    } finally {
      setLoading(false);
    }
  }, [eventId, token, isLoggedIn, permission, notifications, showNotification]);

  // Anmod om notifikationstilladelse ved første load
  useEffect(() => {
    if (isSupported && isLoggedIn && permission === 'default') {
      requestPermission();
    }
  }, [isSupported, isLoggedIn, permission, requestPermission]);

  useEffect(() => {
    loadNotifications();
    
    // Poll for nye notifikationer hvert 30. sekund
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [eventId, token]); // Fjernet loadNotifications fra deps for at undgå infinite loop

  const dismissNotification = async (notificationId) => {
    try {
      await axios.post(`${API}/events/${eventId}/notifications/${notificationId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDismissed(prev => new Set([...prev, notificationId]));
      
      // Hvis det var den sidste, gå til forrige
      if (currentIndex >= notifications.length - 1) {
        setCurrentIndex(Math.max(0, currentIndex - 1));
      }
    } catch (error) {
      console.error("Fejl ved afvisning af notifikation:", error);
    }
  };

  // Filtrer dismissed notifikationer
  const activeNotifications = notifications.filter(n => !dismissed.has(n.id));
  const currentNotification = activeNotifications[currentIndex];

  // Ingen notifikationer at vise
  if (loading || activeNotifications.length === 0) {
    return null;
  }

  const getTypeStyles = (type) => {
    switch(type) {
      case 'urgent':
        return {
          bg: 'bg-red-500/95',
          border: 'border-red-400',
          icon: <AlertTriangle className="h-6 w-6 text-white" />,
          label: 'AKUT'
        };
      case 'important':
        return {
          bg: 'bg-yellow-500/95',
          border: 'border-yellow-400',
          icon: <Bell className="h-6 w-6 text-white" />,
          label: 'VIGTIG'
        };
      default:
        return {
          bg: 'bg-blue-600/95',
          border: 'border-blue-400',
          icon: <MessageSquare className="h-6 w-6 text-white" />,
          label: 'BESKED'
        };
    }
  };

  const styles = getTypeStyles(currentNotification.type);

  // Map position - stort overlay på kortet
  if (position === "map") {
    return (
      <div 
        className="fixed inset-x-4 top-20 z-[9999] max-w-2xl mx-auto"
        data-testid="notification-popup-map"
      >
        <div className={`${styles.bg} ${styles.border} border-2 rounded-xl shadow-2xl overflow-hidden`}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/20">
            <div className="flex items-center gap-3">
              {styles.icon}
              <span className="font-bold text-white tracking-wider">{styles.label}</span>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Lyd toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="text-white hover:bg-white/20 h-8 w-8 p-0"
                title={soundEnabled ? 'Slå lyd fra' : 'Slå lyd til'}
              >
                {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
              
              {activeNotifications.length > 1 && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                    disabled={currentIndex === 0}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-white text-sm">
                    {currentIndex + 1} / {activeNotifications.length}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentIndex(Math.min(activeNotifications.length - 1, currentIndex + 1))}
                    disabled={currentIndex >= activeNotifications.length - 1}
                    className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => dismissNotification(currentNotification.id)}
                className="text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 text-white">
            <h3 className="text-xl font-bold mb-2">
              {currentNotification.subject}
            </h3>
            
            <p className="text-white/90 text-lg leading-relaxed whitespace-pre-wrap">
              {currentNotification.content}
            </p>
            
            <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <Clock className="h-4 w-4" />
                {new Date(currentNotification.created_at).toLocaleString('da-DK')}
              </div>
              <div className="text-white/70 text-sm">
                Fra: {currentNotification.sender_name}
              </div>
            </div>
          </div>
          
          {/* Action Button */}
          <div className="px-6 pb-6">
            <Button
              onClick={() => dismissNotification(currentNotification.id)}
              className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30"
            >
              OK - Luk besked
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Corner position - lille notifikationsindikator
  return (
    <div 
      className="fixed bottom-4 right-4 z-[9999]"
      data-testid="notification-popup-corner"
    >
      <button
        onClick={() => onClose && onClose()}
        className={`${styles.bg} ${styles.border} border-2 rounded-xl p-4 shadow-lg flex items-center gap-3 hover:scale-105 transition-transform`}
      >
        {styles.icon}
        <div className="text-white text-left">
          <p className="font-bold text-sm">{activeNotifications.length} ny(e) besked(er)</p>
          <p className="text-xs text-white/80">Klik for at se</p>
        </div>
      </button>
    </div>
  );
}
